import { useEffect, useState, useRef, useCallback } from 'react';
import Layout from './Layout';
import Loader from './Loader';
import { API_URL } from '../config';
import moment from 'moment';
import { Users, Search, Plus, X, MapPin, Briefcase } from 'lucide-react';

const thClass = "px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider";

const ROLE_IDS = [135, 356, 228, 146, 355, 354, 353, 143, 58];
const PORTFOLIO_CYCLE_LANG_ID = 3569;

// ─── Popover portfolio (rôles uniquement, cycle_lang fixe) ────────
const PortfolioPopover = ({ contact, onClose, onAssigned }) => {
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [saving, setSaving] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    useEffect(() => {
        fetch(`${API_URL}?action=getEventContactTypeList&filter=WHERE id_event_contact_type IN(${ROLE_IDS.join(',')})`)
            .then(r => r.json())
            .then(data => setRoles(Array.isArray(data) ? data.sort((a, b) => a.libelle.localeCompare(b.libelle)) : []))
            .catch(() => { });
    }, []);

    const toggle = (id) => setSelectedRoles(prev =>
        prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );

    const handleAdd = async () => {
        setSaving(true);
        await fetch(`${API_URL}?action=createContactCycleLang`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id_contact: contact.id_contact,
                id_cycle_lang: PORTFOLIO_CYCLE_LANG_ID,
                id_event_contact_type: selectedRoles,
            }),
        });
        setSaving(false);
        onAssigned(contact.id_contact);
        onClose();
    };

    return (
        <div ref={ref} className="absolute z-50 right-0 top-8 w-72 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-gray-200 dark:border-neutral-600 p-3">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5 text-e2o-500" />
                    <p className="text-xs font-semibold text-gray-700 dark:text-neutral-200">Ajouter à mon portfolio</p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-neutral-200">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <ul className="max-h-52 overflow-y-auto divide-y divide-gray-100 dark:divide-neutral-700 mb-3">
                {roles.length === 0 && <li className="py-3 text-xs text-gray-400 text-center">Chargement…</li>}
                {roles.map(r => (
                    <li key={r.id_event_contact_type}>
                        <label className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 rounded transition-colors">
                            <input
                                type="checkbox"
                                checked={selectedRoles.includes(r.id_event_contact_type)}
                                onChange={() => toggle(r.id_event_contact_type)}
                                className="accent-e2o-500 w-4 h-4 shrink-0"
                            />
                            <span className="text-sm text-gray-800 dark:text-neutral-200">{r.libelle}</span>
                        </label>
                    </li>
                ))}
            </ul>
            <button
                onClick={handleAdd}
                disabled={selectedRoles.length === 0 || saving}
                className="w-full py-2 rounded-lg bg-e2o-500 hover:bg-e2o-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
                <Briefcase className="w-4 h-4" />
                {saving ? 'Ajout…' : 'Ajouter au portfolio'}
            </button>
        </div>
    );
};

// ─── Bouton portfolio ─────────────────────────────────────────────
const PortfolioButton = ({ contact, onRefresh }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-e2o-50 dark:bg-e2o-900/20 text-e2o-600 dark:text-e2o-400 hover:bg-e2o-100 dark:hover:bg-e2o-900/40 text-xs font-medium transition-colors whitespace-nowrap"
                title="Ajouter à mon portfolio"
            >
                <Briefcase className="w-3.5 h-3.5 shrink-0" />
                Ajouter à mon portfolio
            </button>
            {open && (
                <PortfolioPopover
                    contact={contact}
                    onClose={() => setOpen(false)}
                    onAssigned={onRefresh}
                />
            )}
        </div>
    );
};

// ─── Popover assignation (2 étapes) ──────────────────────────────
const AssignPopover = ({ contact, cycleLangList, onClose, onAssigned }) => {
    const [step, setStep] = useState(1);
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [q, setQ] = useState('');
    const [saving, setSaving] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    useEffect(() => {
        fetch(`${API_URL}?action=getEventContactTypeList&filter=WHERE id_event_contact_type IN(${ROLE_IDS.join(',')})`)
            .then(r => r.json())
            .then(data => setRoles(Array.isArray(data) ? data.sort((a, b) => a.libelle.localeCompare(b.libelle)) : []))
            .catch(() => { });
    }, []);

    const toggleRole = (id) => {
        setSelectedRoles(prev =>
            prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
        );
    };

    const results = cycleLangList
        .filter(cl => (cl.nom_cycle_lang || '').toLowerCase().includes(q.toLowerCase()))
        .slice(0, 12);

    const handleAssign = async (cl) => {
        setSaving(true);
        await fetch(`${API_URL}?action=createContactCycleLang`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id_contact: contact.id_contact,
                id_cycle_lang: cl.id_cycle_lang,
                id_event_contact_type: selectedRoles,
            }),
        });
        setSaving(false);
        onAssigned(contact.id_contact);
        onClose();
    };

    return (
        <div ref={ref} className="absolute z-50 right-0 top-8 w-80 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-gray-200 dark:border-neutral-600 p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    {step === 2 && (
                        <button onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-600 dark:hover:text-neutral-200">
                            <span className="text-sm">←</span>
                        </button>
                    )}
                    <p className="text-xs font-semibold text-gray-700 dark:text-neutral-200">
                        {step === 1 ? '1 / 2 — Rôle(s)' : '2 / 2 — Plan d\'action'}
                    </p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-neutral-200">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Étape 1 : sélection des rôles */}
            {step === 1 && (
                <>
                    <ul className="max-h-52 overflow-y-auto divide-y divide-gray-100 dark:divide-neutral-700 mb-3">
                        {roles.length === 0 && (
                            <li className="py-3 text-xs text-gray-400 text-center">Chargement…</li>
                        )}
                        {roles.map(r => (
                            <li key={r.id_event_contact_type}>
                                <label className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 rounded transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={selectedRoles.includes(r.id_event_contact_type)}
                                        onChange={() => toggleRole(r.id_event_contact_type)}
                                        className="accent-e2o-500 w-4 h-4 shrink-0"
                                    />
                                    <span className="text-sm text-gray-800 dark:text-neutral-200">{r.libelle}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                    <button
                        onClick={() => setStep(2)}
                        disabled={selectedRoles.length === 0}
                        className="w-full py-2 rounded-lg bg-e2o-500 hover:bg-e2o-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Suivant →
                    </button>
                </>
            )}

            {/* Étape 2 : sélection du plan d'action */}
            {step === 2 && (
                <>
                    <div className="relative mb-2">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                            autoFocus
                            type="text"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Rechercher un plan d'action…"
                            className="w-full pl-7 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-e2o-500"
                        />
                    </div>
                    <ul className="max-h-52 overflow-y-auto divide-y divide-gray-100 dark:divide-neutral-700">
                        {results.length === 0 && (
                            <li className="py-3 text-xs text-gray-400 text-center">Aucun résultat</li>
                        )}
                        {results.map(cl => (
                            <li key={cl.id_cycle_lang}>
                                <button
                                    onClick={() => handleAssign(cl)}
                                    disabled={saving}
                                    className="w-full text-left px-2 py-2 text-sm text-gray-800 dark:text-neutral-200 hover:bg-e2o-50 dark:hover:bg-e2o-900/20 rounded transition-colors flex items-center justify-between gap-2"
                                >
                                    <span className="truncate">{cl.nom_cycle_lang}</span>
                                    {saving && <span className="text-xs text-e2o-500 shrink-0">…</span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

// ─── Modal confirmation suppression ──────────────────────────────
const DeleteConfirmModal = ({ ccl, contact, onCancel, onConfirm }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
        <div className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-red-200 dark:border-red-900">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0">
                    <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">Supprimer l'association</p>
                    <p className="text-xs text-gray-500 dark:text-neutral-400">{contact.prenom} {contact.nom}</p>
                </div>
            </div>
            <div className="flex items-center gap-2 mb-1">
                {ccl.cycle_lang?.logo_better?.small && (
                    <img src={ccl.cycle_lang.logo_better.small} alt="" className="w-8 h-8 object-contain shrink-0" />
                )}
                <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-neutral-300">{ccl.cycle_lang?.nom_cycle_lang}</p>
                    {ccl.event_contact_type?.libelle && (
                        <p className="text-xs text-gray-500 dark:text-neutral-400">Rôle : {ccl.event_contact_type.libelle}</p>
                    )}
                </div>
            </div>
            <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 mb-5">
                Cette action est irréversible.
            </p>
            <div className="flex gap-3">
                <button
                    onClick={onCancel}
                    className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-neutral-600 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
                >
                    Annuler
                </button>
                <button
                    onClick={onConfirm}
                    className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                >
                    Supprimer
                </button>
            </div>
        </div>
    </div>
);

// ─── Cellule collectifs ───────────────────────────────────────────
const CycleLangCell = ({ contact, ccls, cycleLangList, onRefresh }) => {
    const [open, setOpen] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const list = ccls || [];

    const handleDelete = async () => {
        await fetch(`${API_URL}?action=deleteContactCycleLang&id_contact_cycle_lang=${toDelete.id_contact_cycle_lang}`);
        setToDelete(null);
        onRefresh(contact.id_contact);
    };

    return (
        <>
            {toDelete && (
                <DeleteConfirmModal
                    ccl={toDelete}
                    contact={contact}
                    onCancel={() => setToDelete(null)}
                    onConfirm={handleDelete}
                />
            )}
            <div className="flex flex-wrap gap-1 items-center">
                {list.map(ccl => (
                    <span key={ccl.id_contact_cycle_lang}
                        className="inline-flex items-start gap-1 pl-2 pr-1 py-0.5 rounded-lg bg-e2o-100 dark:bg-e2o-900/30 text-xs whitespace-nowrap group">
                        {ccl.cycle_lang?.logo_better?.small && (
                            <img src={ccl.cycle_lang.logo_better.small} alt="" className="w-5 h-5 object-contain shrink-0 mt-0.5" />
                        )}
                        <span className="flex flex-col">
                            <span className="font-medium text-e2o-700 dark:text-e2o-300">
                                {ccl.cycle_lang?.nom_cycle_lang || ccl.id_cycle_lang}
                            </span>
                            {ccl.event_contact_type?.libelle && (
                                <span className="text-e2o-500 dark:text-e2o-400 text-[10px] leading-tight">
                                    {ccl.event_contact_type.libelle}
                                </span>
                            )}
                        </span>
                        <button
                            onClick={() => setToDelete(ccl)}
                            className="mt-0.5 text-e2o-300 dark:text-e2o-600 hover:text-red-500 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            title="Supprimer"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                <div className="relative">
                    <button
                        onClick={() => setOpen(o => !o)}
                        className="w-5 h-5 rounded-full bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-400 hover:bg-e2o-100 hover:text-e2o-600 dark:hover:bg-e2o-900/30 dark:hover:text-e2o-400 transition-colors"
                        title="Assigner un collectif"
                    >
                        <Plus className="w-3 h-3" />
                    </button>
                    {open && (
                        <AssignPopover
                            contact={contact}
                            cycleLangList={cycleLangList}
                            onClose={() => setOpen(false)}
                            onAssigned={onRefresh}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

// ─── Composant principal ──────────────────────────────────────────
const CheckInscriptions = () => {
    const [inscriptions, setInscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [includeCanada, setIncludeCanada] = useState(false);

    const [contactCycleLangMap, setContactCycleLangMap] = useState({});
    const [cycleLangList, setCycleLangList] = useState([]);
    const [showIds, setShowIds] = useState(false);

    // Charge les cycle_langs disponibles une seule fois
    useEffect(() => {
        fetch(`${API_URL}?action=getCycleLang&filter=cycle_lang_type IN('collectif_planaction') AND cycle_lang_state='active' AND &get_parents=0&get_children=0`)
            .then(r => r.json())
            .then(data => setCycleLangList(Array.isArray(data) ? data : []))
            .catch(() => { });
    }, []);

    const buildMap = (partenaires, contacts) => {
        const map = {};
        partenaires.forEach(p => {
            const id = p.contact.id_contact;
            if (!map[id]) map[id] = { ...p, libelles: [], confEventIds: [] };
            const libelle = p.conferencier_statut?.libelle;
            if (libelle && !map[id].libelles.includes(libelle)) map[id].libelles.push(libelle);
            const ceId = p.conf_event?.id_conf_event ?? p.id_conf_event;
            if (ceId && !map[id].confEventIds.includes(String(ceId))) map[id].confEventIds.push(String(ceId));
        });
        contacts.forEach(c => {
            if (!map[c.id_contact]) {
                map[c.id_contact] = {
                    contact: { ...c, email: c.mail },
                    logos: { small: c.photos?.small },
                    id_conferencier: null,
                    date_creation: c.date_creat,
                    libelles: [],
                    confEventIds: [],
                };
            }
        });
        return Object.values(map).sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));
    };

    const fetchContactCycleLangs = useCallback(async (ids) => {
        if (!ids.length) return;
        const res = await fetch(
            `${API_URL}?action=getContactCycleLang&filter= id_contact IN (${ids.join(',')} )`
        );
        const data = await res.json();
        const isValid = ccl => !(String(ccl.id_cycle_lang) === '0' && String(ccl.id_cycle_lang_2) === '0');
        const map = {};
        (Array.isArray(data) ? data : []).filter(isValid).forEach(ccl => {
            const id = ccl.contact?.id_contact;
            if (!id) return;
            if (!map[id]) map[id] = [];
            map[id].push(ccl);
        });
        setContactCycleLangMap(map);
    }, []);

    const refreshContactCycleLang = useCallback(async (id_contact) => {
        const res = await fetch(
            `${API_URL}?action=getContactCycleLang&filter= id_contact=${id_contact}`
        );
        const data = await res.json();
        setContactCycleLangMap(prev => ({
            ...prev,
            [id_contact]: (Array.isArray(data) ? data : []).filter(
                ccl => !(String(ccl.id_cycle_lang) === '0' && String(ccl.id_cycle_lang_2) === '0')
            ),
        }));
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const baseParams = `DATE(date_creat) >= DATE_SUB(CURDATE(), INTERVAL 24 MONTH) ORDER BY date_creat DESC`;
                const sourceFilter = `source !=''`;
                const canadaFilter = `c.pays IN ('Canada', 'Quebec', 'Québec')`;

                const contactQueries = [
                    fetch(`${API_URL}?action=getContactList&params=WHERE ${sourceFilter} AND ${baseParams} AND ${canadaFilter}`),
                    fetch(`${API_URL}?action=getPartenairesLight&params=AND c.${sourceFilter} AND DATE(cf.date_creation) >= DATE_SUB(CURDATE(), INTERVAL 24 MONTH) AND ${canadaFilter}`),
                ];
                if (includeCanada) {
                    contactQueries.push(
                        fetch(`${API_URL}?action=getContactList&params=WHERE ${canadaFilter} AND ${baseParams}`)
                    );
                }

                const responses = await Promise.all(contactQueries);
                const [contactsData, partenairesData, canadaData] = await Promise.all(responses.map(r => r.json()));

                const contacts = [
                    ...(Array.isArray(contactsData) ? contactsData : []),
                    ...(Array.isArray(canadaData) ? canadaData : []),
                ];
                const partenaires = Array.isArray(partenairesData) ? partenairesData : [];

                const result = buildMap(partenaires, contacts);
                setInscriptions(result);

                const ids = result.map(p => p.contact.id_contact).filter(Boolean);
                fetchContactCycleLangs(ids);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [includeCanada, fetchContactCycleLangs]);

    const filtered = inscriptions.filter(p => {
        const q = search.toLowerCase();
        return (
            `${p.contact.prenom} ${p.contact.nom}`.toLowerCase().includes(q) ||
            (p.contact.societe || '').toLowerCase().includes(q) ||
            (p.contact.email || p.contact.mail || '').toLowerCase().includes(q)
        );
    });

    return (
        <Layout>
            <div className=" mx-auto px-4 py-8">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Inscriptions — 3 derniers mois
                </h1>

                {!loading && !error && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-neutral-700 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-e2o-100 dark:bg-e2o-900/30 flex items-center justify-center shrink-0">
                                <Users className="w-5 h-5 text-e2o-600 dark:text-e2o-400" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-neutral-400">Total contacts</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{inscriptions.length}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Barre outils */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher nom, société, email…"
                            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-e2o-500 text-sm"
                        />
                    </div>

                    <button
                        onClick={() => setShowIds(v => !v)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-colors ${showIds ? 'border-e2o-500 text-e2o-600 bg-e2o-50 dark:bg-e2o-900/20 dark:text-e2o-400' : 'border-gray-200 dark:border-neutral-600 text-gray-500 dark:text-neutral-400'}`}
                    >
                        IDs {showIds ? '▴' : '▾'}
                    </button>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <div
                            onClick={() => setIncludeCanada(v => !v)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${includeCanada ? 'bg-e2o-500' : 'bg-gray-200 dark:bg-neutral-600'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${includeCanada ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-neutral-300">
                            <MapPin className="w-4 h-4 text-red-500" />
                            Canada / Québec
                        </div>
                    </label>
                </div>

                {loading && <div className="text-center py-10"><Loader /></div>}
                {error && <p className="text-red-500 text-center py-6">{error}</p>}

                {!loading && !error && (
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                            <thead className="bg-gray-50 dark:bg-neutral-900">
                                <tr>
                                    {showIds && <th className={`${thClass} hidden md:table-cell`}>IDs</th>}
                                    <th className={thClass}>Contact</th>
                                    <th className={`${thClass} hidden md:table-cell`}>Statut</th>
                                    <th className={`${thClass} hidden md:table-cell`}>Collectifs</th>
                                    <th className={thClass}>Inscription</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={showIds ? 5 : 4} className="px-4 py-10 text-center text-gray-400 dark:text-neutral-500">
                                            Aucune inscription trouvée
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((p, i) => (
                                    <tr key={`${p.contact.id_contact}-${i}`}
                                        className="hover:bg-gray-50 dark:hover:bg-neutral-700/40 transition-colors">

                                        {/* IDs (toggle) */}
                                        {showIds && (
                                            <td className="px-4 py-3 hidden md:table-cell font-mono align-top">
                                                <p className="text-xs text-gray-400 dark:text-neutral-500"><span className="text-gray-300 dark:text-neutral-600">c </span>{p.contact.id_contact}</p>
                                                {p.id_conferencier && <p className="text-xs text-gray-300 dark:text-neutral-600"><span className="text-gray-200 dark:text-neutral-700">cf </span>{p.id_conferencier}</p>}
                                            </td>
                                        )}

                                        {/* Contact — nom, société, email, ville, source, pays, portfolio */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                {p.logos?.small ? (
                                                    <img src={p.logos.small} alt="" className="w-9 h-9 rounded-full object-cover bg-gray-100 shrink-0 mt-0.5" />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-full bg-e2o-100 dark:bg-e2o-900/30 flex items-center justify-center shrink-0 text-e2o-600 dark:text-e2o-400 font-semibold text-sm mt-0.5">
                                                        {(p.contact.prenom?.[0] || '?').toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="min-w-0 space-y-0.5">
                                                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                                        {p.contact.prenom} {p.contact.nom}
                                                    </p>
                                                    <p className="text-sm text-gray-700 dark:text-neutral-300 truncate">
                                                        {p.contact.societe ? p.contact.societe.substring(0, 28) + (p.contact.societe.length > 28 ? '…' : '') : '—'}
                                                    </p>
                                                    <p className="text-xs text-gray-400 dark:text-neutral-500 truncate">
                                                        {p.contact.mail || p.contact.email || ''}
                                                    </p>
                                                    {p.contact.ville && (
                                                        <p className="text-xs text-gray-300 dark:text-neutral-600 truncate">{p.contact.ville}</p>
                                                    )}
                                                    <div className="flex items-center gap-2 pt-0.5 flex-wrap">
                                                        {p.contact.source && (
                                                            <span className="inline-block px-1.5 py-0.5 rounded-full bg-e2o-100 dark:bg-e2o-900/30 text-e2o-700 dark:text-e2o-300 text-xs font-medium">
                                                                {p.contact.source}
                                                            </span>
                                                        )}
                                                        {p.contact.flag && (
                                                            <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-neutral-500">
                                                                <span>{p.contact.flag}</span>
                                                                <span>{p.contact.pays}</span>
                                                            </span>
                                                        )}
                                                        <PortfolioButton contact={p.contact} onRefresh={refreshContactCycleLang} />
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Statut — badges verticaux */}
                                        <td className="px-4 py-3 hidden md:table-cell align-top">
                                            <div className="flex flex-col gap-1">
                                                {p.libelles.length > 0
                                                    ? p.libelles.map(l => (
                                                        <span key={l} className="inline-block px-2 py-0.5 rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-neutral-300 text-xs">
                                                            {l}
                                                        </span>
                                                    ))
                                                    : <span className="text-xs text-gray-300 dark:text-neutral-600">—</span>
                                                }
                                            </div>
                                        </td>

                                        {/* Collectifs — badges verticaux + assignation */}
                                        <td className="px-4 py-3 hidden md:table-cell align-top text-sm">
                                            <CycleLangCell
                                                contact={p.contact}
                                                ccls={contactCycleLangMap[p.contact.id_contact]}
                                                cycleLangList={cycleLangList}
                                                onRefresh={refreshContactCycleLang}
                                            />
                                        </td>

                                        {/* Date */}
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-neutral-300 whitespace-nowrap align-top">
                                            {moment(p.date_creation).locale('fr').format('DD MMM YYYY')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default CheckInscriptions;
